import { useReCaptcha } from 'next-recaptcha-v3';
import { useCallback, useEffect, useState, KeyboardEvent } from 'react';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ArrowRightLarge } from '@/ui/Icons/ArrowRightLarge';
import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';

import { trackError } from '@/utils/errors';
import { postData } from '@/utils/fetcher';
import { routes } from '@/utils/routes';
import { validateEmail } from '@/utils/validateEmail';

import { LoadingSpinnerPrimary } from '../../ui/Icons/Animated/LoadingSpinner';

export const NewsletterInput = (): React.ReactElement => {
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { executeRecaptcha, loaded, error: recaptchaError } = useReCaptcha();

  const handleReCaptchaVerify = useCallback(
    async (action: string) => {
      if (!executeRecaptcha || !loaded || recaptchaError) return;

      try {
        // immediately verify the user via reCAPTCHA v3
        const { success } = await postData<{ success: boolean }>(routes.api.GOOGLE_TOKEN, {
          arg: { token: await executeRecaptcha(action) },
        });

        setVerified(success);
      } catch (error) {
        if (error instanceof Error) {
          trackError(error);
        }
        setVerified(false);
      }
    },
    [executeRecaptcha, loaded, recaptchaError],
  );

  useEffect(() => {
    if (!executeRecaptcha || !loaded || recaptchaError) return;

    handleReCaptchaVerify('MatchaNewsletterFormLoaded');
  }, [executeRecaptcha, handleReCaptchaVerify, loaded, recaptchaError]);

  const onSubmitEmail = async () => {
    const isValidEmail = validateEmail(inputValue);
    if (!isValidEmail) {
      setError('Invalid email');
      return;
    }

    try {
      setIsSubmitting(true);

      if (!verified || !executeRecaptcha || !loaded || recaptchaError) return;

      const response = await postData<Response>(routes.api.EMAIL, {
        arg: { email: inputValue, token: await executeRecaptcha('MatchaNewsletterFormSubmitted') },
      });
      setSubmitted(true);

      if (response.status === 400) setError('An error has occurred. Please try again later.');
    } catch (error) {
      if (error instanceof Error) {
        trackError(error);
      }
      setError('An error has occurred. Please try again later.');
    } finally {
      // submit finished
      setIsSubmitting(false);
    }
  };

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') onSubmitEmail();
  }

  return (
    <>
      <div className="mx-auto flex items-center relative z-[1] max-w-[296px] w-full tablet:max-w-[430px] tablet:flex-grow h-fit">
        <input
          placeholder="Email address"
          className={fontStyles({
            variant: 'BODY_LG',
            class: [
              'bg-token-alphalight-200',
              'rounded-lg',
              'h-13',
              'border',
              'border-token-gray-200',
              'w-full',
              'text-token-gray-500',
              'text-base',
              'pl-3',
              'pr-10',
              'tablet:pl-5',
              'tablet:pr-16',
              'box-border',
              'placeholder:text-token-gray-700',
              'hover:border-token-gray-600',
              'focus:bg-primitive-basic-white',
              'focus:outline',
              'focus:outline-2',
              'focus:outline-token-purple-1000',
              'active:border-transparent',
              'disabled:cursor-not-allowed',
              error
                ? 'border-token-red-600 focus:outline-none hover:border-token-red-600 border-2'
                : '',
            ],
          })}
          value={inputValue}
          onChange={(e) => {
            if (submitted) {
              setSubmitted(false);
            }
            setError('');
            setInputValue(e.target.value);
          }}
          disabled={isSubmitting}
          type="email"
          onKeyDown={handleKeyDown}
        />
        {!isSubmitting && !submitted && verified ? (
          <button
            className={buttons({
              size: 'small',
              color: 'gray',
              shape: 'rounded',
              iconOnly: true,
              class: [
                'absolute',
                'right-2', // move arrow icon slightly left
                'tablet:right-4', // move arrow icon left on tablet
                'bg-transparent',
                'px-2',
                'py-2',
                '[.light_&]:hover:bg-token-gray-500',
              ],
            })}
            onClick={onSubmitEmail}
            aria-label="Submit email"
          >
            <ArrowRightLarge />
          </button>
        ) : (
          <div
            className={buttons({
              size: 'small',
              color: 'gray',
              shape: 'rounded',
              iconOnly: true,
              class: ['absolute', 'right-2', 'tablet:right-4', 'bg-token-gray-500', 'p-2'],
            })}
          >
            {(isSubmitting || !verified) && <LoadingSpinnerPrimary className="mx-auto h-6 w-6" />}
            {submitted && <CheckmarkIcon className="size-6" />}
          </div>
        )}
      </div>
      {error && (
        <div className="text-token-red-1000 mt-6 ml-5 max-w-[296px] w-full text-left tablet:max-w-[430px] tablet:flex-grow">
          {error}
        </div>
      )}
    </>
  );
};
